/**
 * Job detail, on real data only.
 *
 * Every value comes from the API: the job (`GET /jobs/{id}`), its step
 * rows (`GET /jobs/{id}/steps`) and the approval waiting on it
 * (`GET /approvals?job_id=`). There is no simulator and no fallback
 * job: an id the API does not return renders "Job not found".
 *
 * The Live view panel shows the runner's browser while a supervisor is
 * watching (never recorded) and is where a supervisor takes control.
 * While the job waits for a person, a banner at the top says so and
 * links to that panel.
 *
 * Not shown, because nothing can supply it: stored screenshots (the
 * runner uploads none), patient details (the API returns none) and job
 * inputs.
 *
 * Loading / not found / error / permission-denied per Rule Zero #8.
 */
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Hand } from "lucide-react";

import { ApprovalPanel } from "./ApprovalPanel";
import { FailurePanel } from "./FailurePanel";
import { JobControls } from "./JobControls";
import { isFinished, useJobApprovals, useJobDetail, type JobDetail } from "./jobApi";
import { JobStatusPill, JobSummary } from "./JobSummary";
import { useLiveState } from "./liveApi";
import { LiveViewPanel } from "./LiveViewPanel";
import { DeniedNotice, ErrorNotice, Skeleton } from "./parts";
import { PhaseList } from "./PhaseList";
import { StepTimeline } from "./StepTimeline";
import { BTN, isStatus, LIVE_VIEW_ID } from "./ui";

export function JobDetailScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const query = useJobDetail(jobId);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex items-center gap-3 border-b border-paper-line bg-paper-0 px-6 py-3">
        <button type="button" onClick={() => navigate(-1)} className={BTN}>
          <ArrowLeft size={14} aria-hidden /> Back
        </button>
        <h1 className="min-w-0 flex-1 truncate text-md font-medium text-ink-1">
          {query.data ? query.data.workflow.name : "Job"}
        </h1>
        {query.data && (
          <JobStatusPill status={query.data.status} apiStatus={query.data.apiStatus} />
        )}
      </header>

      <main className="flex-1 overflow-auto p-6">
        {query.isPending ? (
          <LoadingState />
        ) : query.data ? (
          <>
            {query.isError && (
              <p role="status" className="mx-auto mb-3 max-w-6xl text-xs text-status-failed">
                The last refresh failed. Showing the most recent data received.
              </p>
            )}
            <WaitingBanner job={query.data} />
            <JobBody job={query.data} />
          </>
        ) : isStatus(query.error, 404, 400) ? (
          // One message for "no such job" and "not yours": the API
          // answers both the same way on purpose.
          <Centered>
            <p className="text-md text-ink-1">Job not found.</p>
            <p className="mt-1 text-sm text-ink-3">
              It does not exist, or it belongs to an entity you don't have access to.
            </p>
          </Centered>
        ) : isStatus(query.error, 403) ? (
          <Centered>
            <DeniedNotice what="jobs" roles="viewer, operator, supervisor or entity admin" />
          </Centered>
        ) : (
          <Centered>
            <ErrorNotice
              message="The job could not be loaded."
              error={query.error}
              onRetry={() => query.refetch()}
            />
          </Centered>
        )}
      </main>
    </div>
  );
}

function JobBody({ job }: { job: JobDetail }) {
  const live = !isFinished(job.status);
  const approvals = useJobApprovals(job.id, live);
  const awaiting = approvals.data?.items.find((a) => a.state === "pending");
  // Shared with the Live view panel (same query key, one poll).
  const control = useLiveState(job.id, live);

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="space-y-4">
        <JobSummary job={job} />
        {job.resultSummary && <FailurePanel reason={job.resultSummary} />}
        <ApprovalPanel query={approvals} live={live} />
        <JobControls job={job} stopRequested={control.data?.stopRequested ?? false} />
      </div>
      <div className="space-y-4">
        <LiveViewPanel jobId={job.id} jobActive={live} />
        <PhaseList job={job} awaitingPhaseId={awaiting?.phase?.id ?? null} />
        <StepTimeline jobId={job.id} phases={job.phaseDetails} live={live} />
        <p className="text-xs text-ink-3">
          Screenshots are not stored, so none are shown. The live view is not recorded.
        </p>
      </div>
    </div>
  );
}

/**
 * Shown while the job page is polling and the runner is waiting for a
 * person (`waiting_for_human`). Links to the Live view panel, where the
 * person watches and takes control. Gone once someone takes control
 * (`paused_for_takeover`) or the job moves on.
 */
export function WaitingBanner({ job }: { job: JobDetail }) {
  if (isFinished(job.status) || job.apiStatus.trim().toLowerCase() !== "waiting_for_human") {
    return null;
  }
  return (
    <div
      role="status"
      className="mx-auto mb-4 flex max-w-6xl items-center gap-2 rounded-md border border-status-takeover bg-status-takeover-bg px-4 py-2 text-sm text-ink-1"
    >
      <Hand size={14} className="shrink-0 text-status-takeover" aria-hidden />
      <a
        href={`#${LIVE_VIEW_ID}`}
        className="font-medium underline-offset-2 hover:underline"
        onClick={(event) => {
          // The page scrolls inside <main>, not the window: scroll the
          // panel into view and move focus to it.
          const panel = document.getElementById(LIVE_VIEW_ID);
          if (!panel) return;
          event.preventDefault();
          panel.scrollIntoView?.({ behavior: "smooth", block: "start" });
          panel.setAttribute("tabindex", "-1");
          panel.focus({ preventScroll: true });
        }}
      >
        Waiting for you: Watch live, then Take control
      </a>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto mt-16 max-w-md rounded-md border border-paper-line bg-paper-2 p-6 text-center [&>div]:text-left">
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div
      role="status"
      aria-busy="true"
      className="mx-auto grid max-w-6xl grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
    >
      <span className="sr-only">Loading job</span>
      <div className="space-y-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-28" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}
